import { ReactComponent as IconDownload } from "@/assets/svgs/download-white.svg";
import { ReactComponent as IconCopy } from "@/assets/svgs/icon-document-duplicate.svg";
import { ReactComponent as IconChevronLeft } from "@/assets/svgs/icon-path.svg";
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg';
import Button from "@/components/Button/index";
import Input from "@/components/Input";
import Toast from '@/components/Toast';
import addressTypes from '@/constants/address-type';
import fileTypes from "@/constants/file-type/index";
import toastType from "@/constants/toast-type/index";
import useConfig from '@/hooks/use-config';
import useDid from "@/hooks/use-did/index";
import useEtr from "@/hooks/use-etr/index";
import useModal from '@/hooks/use-modal';
import usePagination from '@/hooks/use-pagination';
import useToast from '@/hooks/use-toast';
import classname from 'classnames';
import { produce } from 'immer';
import { debounce, filter, orderBy, uniqueId } from "lodash";
import { useEffect, useRef, useState } from "react";
import DownloadModal from "./components/download-modal";
import TableRow from "./components/table-row";
import TableRowInput from "./components/table-row-input";
import { feature, features } from "@/utils/feature-verifier";
import Search from '@/componentsDS/Search/index';

const AddressBookPage = () => {

	/*=================================================
		General
	/================================================*/
	const [activeAddress, setActiveAddress] = useState(addressTypes.all)
	const [combineAddress, setCombineAddress] = useState([])
	const [filterAddress, setFilterAddress] = useState([])
	const [searchText, setSearchText] = useState('');
	const [displayedSearchText, setDisplayedSearchText] = useState('');

	const [sortColumn, setSortColumn] = useState('name')
	const [sortOrder, setSortOrder] = useState('asc')

	const dataElementId = "ebl"
	/*=================================================
		API
	/================================================*/
	const { toastList, addToastMessage } = useToast()

	const { identity, provideItems, consumeItems, error: errorConfig } = useConfig()
	const { toFindDid } = useDid()
	const { fetchEtr, etrData, etrErrorMsg } = useEtr({
		dataElementId,
		configData: {
			provideItems,
			identity
		}
	})

	useEffect(() => {
		if (provideItems && identity) {
			fetchEtr()
		}
	}, [provideItems, identity])

	const enrolledDataElement = provideItems.some(item => item.id === dataElementId) || consumeItems.some(item => item.id === dataElementId)

	useEffect(() => {
		setFilterAddress(etrData)
		setCombineAddress(etrData)
		resetState()
	}, [etrData])

	useEffect(() => {
		addToastMessage(errorConfig, toastType.error)
	}, [errorConfig])

	useEffect(() => {
		addToastMessage(etrErrorMsg, toastType.error)
	}, [etrErrorMsg])

	/*=================================================
		Modal
	/================================================*/
	const { visible: downloadModal, show: showDownloadModal, hide: hideDownloadModal } = useModal()

	/*=================================================
		Switch Tab
	/================================================*/
	const addressesValue = [
		{ label: 'All', value: addressTypes.all },
		{ label: 'Saved', value: addressTypes.saved },
		{ label: 'Pitstops', value: addressTypes.pitstop },
	];

	function handleTabClick(addressType) {
		if (addressType === addressTypes.all) {
			setFilterAddress(combineAddress);
		} else {
			const data = produce(combineAddress, (draftData) => {
				return draftData.filter((address) => address.type === addressType);
			});
			setFilterAddress(data);
		}
		goToPage(1)
		setInputValue(1)
		setActiveAddress(addressType)
	}

	/*=================================================
		Filter
	/================================================*/
	const [searchTimeout, setSearchTimeout] = useState(null);

	const handleSearch = (value) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		setSearchTimeout(
			setTimeout(() => {
				setSearchText(value);
				goToPage(1);
				setInputValue(1);
			}, 500)
		);
	};

	const filterData = filter(filterAddress, item => {
		const lowerSearchText = searchText.toLowerCase()
		const lowerName = item?.name?.toLowerCase()
		const lowerEmail = item?.email?.toLowerCase()
		const lowerWalletAddress = item?.walletAddress?.toLowerCase()

		return (
			lowerName?.includes(lowerSearchText) ||
			lowerEmail?.includes(lowerSearchText) ||
			lowerWalletAddress?.includes(lowerSearchText)
		)
	})

	const sortedData = orderBy(
		filterData,
		[
			(option) => !option?.isPinned,
			(option) => option?.[sortColumn]?.toLowerCase(),
		],
		["asc", sortOrder]
	);

	function sortOrderClassName(column) {
		return classname({
			'address-book__head__icon__sort-order': true,
			[`address-book__head__icon__sort-order--${sortOrder}`]: sortColumn === column
		})
	}

	/*=================================================
		Pagination
	/================================================*/
	const itemsPerPage = 10;
	const { currentPage, totalPages, currentItems, goToPage, nextPage, prevPage } = usePagination(sortedData, itemsPerPage);
	const [inputValue, setInputValue] = useState(currentPage.toString());

	function handleSort(column) {
		if (column === sortColumn) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
		} else {
			setSortColumn(column)
			setSortOrder('asc')
		}
		goToPage(1)
	}

	function handlePrev() {
		if (currentPage === 1) {
			return
		}

		prevPage()
		setInputValue(currentPage - 1);
	}

	function handleNext() {
		if (currentPage === totalPages) {
			return
		}

		nextPage()
		setInputValue(currentPage + 1);
	}

	function handlePageInput(value) {
		setInputValue(value);
		handlePageInputDebounced(value);
	}

	const handlePageInputDebounced = debounce((value) => {
		if (value > 0 && value <= totalPages) {
			goToPage(value);
		}
	}, 500);

	const resetFunction = useRef(null);

	/*=================================================
		Additional Function
	/================================================*/
	function checkDuplicate(name, key) {
		const duplicate = combineAddress.find(address => address[key].toLowerCase() === name.toLowerCase())
		return Boolean(duplicate)
	}

	function downloadType(type) {
		const downloadWallet = {
			"Name": identity?.name,
			"Wallet Address": toFindDid(identity?.organization.id)
		}

		if (type === fileTypes.json) {
			const contentType = 'application/json;charset=utf-8';
			const jsonStr = JSON.stringify(downloadWallet, null, 2);

			return new Blob([jsonStr], { type: contentType });
		}

		if (type === fileTypes.csv) {
			const contentType = 'text/csv;charset=utf-8';
			const keys = Object.keys(downloadWallet);
			const values = keys.map(key => downloadWallet[key]);
			const csvStr = `${keys.join(',')}\n${values.join(',')}`;

			return new Blob([csvStr], { type: contentType });
		}
	}

	function handleDownloadIdentity(type) {
		const fileName = `identity.${type}`;
		const url = URL.createObjectURL(downloadType(type));
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;

		document.body.appendChild(a);
		a.click();

		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	function resetState() {
		setActiveAddress(addressTypes.all)

		setSearchText("")
		setDisplayedSearchText("")

		setSortColumn("name")
		setSortOrder("asc")

		goToPage(1)
		setInputValue(1)
	}

	const columnWidth = [
		{
			column: 'name',
			style: {
				minWidth: '150px',
				width: `calc((100% - 400px)/2)`,
			}
		},
		{
			column: 'walletAddress',
			style: {
				minWidth: '350px',
			}
		},
		{
			column: 'email',
			style: {
				minWidth: '150px',
				width: `calc((100% - 400px)/2)`,
			}
		},
		{
			column: 'action',
			style: {
				minWidth: '50px',
			}
		},
	]

	const renderAddressTable = () => {
		if (currentItems && currentItems.length === 0 && searchText !== "") {
			return (
				<div className="address-book__empty">
					No matches for "{searchText}"
				</div>
			);
		};

		return (
			<div className="address-book__table" data-testid="address-book__table">
				<div className="address-book__table__row address-book__table__row--header">
					<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'name')?.style }}>
						<span onClick={() => handleSort('name')} onKeyDown={() => handleSort('name')} data-testid="sort-name">
							Organisation Name<span className={sortOrderClassName('name')}></span>
						</span>
					</div>
					<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'walletAddress')?.style }}>
						<span onClick={() => handleSort('walletAddress')} onKeyDown={() => handleSort('walletAddress')} data-testid="sort-wallet">
							Wallet Address<span className={sortOrderClassName('walletAddress')}></span>
						</span>
					</div>
					<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'email')?.style }}>
						<span onClick={() => handleSort('email')} onKeyDown={() => handleSort('email')} data-testid="sort-email">
							Email<span className={sortOrderClassName('email')}></span>
						</span>
					</div>
					<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'action')?.style }}></div>
				</div>

				{currentPage === 1 && searchText === '' && activeAddress === addressTypes.all && (
					<TableRowInput
						resetCallback={(fn) => (resetFunction.current = fn)}
						fetchEtr={fetchEtr}
						checkDuplicate={checkDuplicate}
						addToastMessage={addToastMessage}
						columnWidth={columnWidth}
					/>
				)}

				{currentItems?.map((address) => {
					const id = uniqueId()

					return (
						<TableRow
							data={address}
							key={id}
							onEdit={resetFunction}
							fetchEtr={fetchEtr}
							checkDuplicate={checkDuplicate}
							addToastMessage={addToastMessage}
							columnWidth={columnWidth}
						/>
					)
				})}
			</div>
		);
	}

	return (
		<>
			<div className="address-book">
				<div className="address-book__header">
					<div className="address-book__my-wallet">
						<div className="address-book__my-wallet__title">
							My Wallet Address <IconDownload
								className="address-book__my-wallet__icon"
								data-testid="address-book__my-wallet__icon"
								onClick={() => { showDownloadModal() }}
							/>
						</div>
						<div className="address-book__my-wallet__content">
							<div className="address-book__my-wallet__content__cols">
								<div className="address-book__my-wallet__content__col">
									<div className="address-book__my-wallet__content__rows">
										<div className="address-book__my-wallet__content__row">
											<div className="address-book__my-wallet__content__label--caption-heavy">
												Name
											</div>
										</div>
										<div className="address-book__my-wallet__content__row">
											<div className="address-book__my-wallet__content__label">
												{identity?.name}
											</div>
										</div>
									</div>
								</div>
								<div className="address-book__my-wallet__content__col">
									<div className="address-book__my-wallet__content__rows">
										<div className="address-book__my-wallet__content__row">
											<div className="address-book__my-wallet__content__label--caption-heavy">
												Wallet Address
											</div>
										</div>
										<div className="address-book__my-wallet__content__row">
											<div className="address-book__my-wallet__content__label">
												{toFindDid(identity?.organization.id)} <IconCopy
													className="address-book__my-wallet__icon"
													data-testid="address-book__copy__icon"
													onClick={async () => {
														try {
															await navigator.clipboard.writeText(toFindDid(identity?.organization.id));
															addToastMessage("Copied to clipboard", toastType.success)
														} catch (error) {
															addToastMessage("Failed to copy to clipboard. Please copied manually.", toastType.error)
															console.error('Failed to write to clipboard: ', error);
														}
													}}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<hr />
				<div className="address-book__content">
					<div className="address-book__search">
						<div style={{width:"350px"}}>
							<Search
								value={displayedSearchText}
								placeholder="Search by name, blockchain address, or email"
								onChange={(e) => {
									setDisplayedSearchText(e.target.value);
									handleSearch(e.target.value);
								}}
								onCancel={() => {
									setDisplayedSearchText('')
									handleSearch('')
								}}
							/>
						</div>

					</div>
					<div className="address-book__tabs">
						<div className="address-book__tabs__left">
							<div className="address-book__tabs__left__title">
								Addresses:
							</div>
							<div className="address-book__tabs__left__switch">
								{addressesValue.map((address) => {
									const id = uniqueId()

									return (
										<div
											key={id}
											className={`address-book__header__switch ${activeAddress === address.value ? 'address-book__header__switch--active' : ''}`}
											onClick={() => handleTabClick(address.value)}
											onKeyDown={() => handleTabClick(address.value)}
											data-testid="address-tab"
										>
											{address.label}
										</div>
									)
								})}
							</div>
						</div>
						<div className="address-book__tabs__right">
							<div className="address-book__tabs__meta">
								<Input
									value={inputValue}
									addClass="pagination__input"
									maxWidth="40"
									onChange={(e) => { handlePageInput(e.target.value) }}
									type="number"
									disabled={!enrolledDataElement || totalPages === 1}
									data-testid="page-input"
								/>
								of {totalPages} pages
							</div>
							<Button
								grey
								addClass="pagination__button pagination__button--prev"
								submit={() => handlePrev()}
								disabled={currentPage === 1}
								text={<IconChevronLeft />}
								data-testid="prev-button"
							/>
							<Button
								grey
								addClass="pagination__button pagination__button--next"
								submit={() => handleNext()}
								disabled={currentPage === totalPages}
								text={<IconChevronLeft />}
								data-testid="next-button"
							/>
						</div>
					</div>

					{enrolledDataElement && renderAddressTable()}
					{!enrolledDataElement && (
						<div className="address-book__empty address-book__empty--center">
							Your pitstop have not been enrolled to any E-Transferable Record enabled Data Element yet.<br />
							Kindly contact your organisation's administrator for enrolment.
						</div>
					)}
				</div>
			</div>

			<DownloadModal
				downloadModal={downloadModal}
				hideDownloadModal={hideDownloadModal}
				handleDownloadIdentity={handleDownloadIdentity}
			/>

			<Toast toastList={toastList} />
		</>
	)
}

export default AddressBookPage;
